'use es6';

import { getBoundsFuncForConfig, getElementsFuncForConfig } from 'layout-dnd-utils/nodePositions/nodePositionUtils';
import { isSameBoundingRect } from 'layout-dnd-utils/geometry/basic';

// Global that tracks all incrementally "next-tick" deferred timers. It is intentionally
// shared across different calls to incrementalSearchForChangedBounds, because we currently
// assume finding changed bounds anywhere triggers new node position looks _for everything_.
let allDeferredTimers = [];
export const basicCancelAndClearAllDeferredTimers = () =>
// Available parameters but unused by the basic implementation
// { nodePositionSubConfig }
{
  allDeferredTimers.forEach(timerId => {
    clearTimeout(timerId);
  });
  allDeferredTimers = [];
};
const basicDeferWork = ({
  timerId
  // Available parameters  but unused by the basic implementation
  // nodePositionSubConfig,
}) => {
  allDeferredTimers.push(timerId);
};

/**
 * Incrementally search for changes to bounds in a specific set of items (that were already determined
   to be on screen in the current viewport elsewhere)
 * @param  {Array} onScreenTuplesToCheck [[item1, previousNodePosition], [item2, previousNodePosition], ...]
 * @param  {number} index current index to check in the onScreenTuplesToCheck array
 * @param  {Object} options (options defined below)
 *   @prop {func} onPositionChanged callback called when the first item with changed position is found
 *   @prop {func} onFinishWithNoChange callback called when all items have been checked and no new position found
 *   @prop {func} onDeferWork (optional) overrides basicDeferWork
 *   @prop {func} onCancelAndClearAllDeferredTimers (optional) overrides basicCancelAndClearAllDeferredTimers
 *   @prop {func} batchSize (optional) number of items to look for bounds before deferring (defaults to 25)
 *   @prop {Object} nodePositionSubConfig (relevant config options defined below)
 *     @prop {func} getElements: returns a list of all corresponding DOM elements for an item
 *     @prop {func} getElementBounds (optional) returns the bounds for the given element
 *     @prop {bool} debug (optional) enables console logging
 * @return {func}
 */
export const incrementalSearchForChangedBounds = (onScreenTuplesToCheck, index, options) => {
  const {
    nodePositionSubConfig,
    onPositionChanged,
    onFinishWithNoChange,
    onDeferWork = basicDeferWork,
    onCancelAndClearAllDeferredTimers = basicCancelAndClearAllDeferredTimers,
    batchSize = 25
  } = options;
  const shouldDeferNextIterationBatch = index !== 0 && index % batchSize === 0;
  if (onScreenTuplesToCheck[index]) {
    const [item, ...itemBounds] = onScreenTuplesToCheck[index];
    const currentElements = getElementsFuncForConfig(nodePositionSubConfig)(item);
    if (currentElements && currentElements.length > 0) {
      for (let elementIndex = 0; elementIndex < currentElements.length; elementIndex++) {
        const element = currentElements[elementIndex];
        const previousBoundsForThisElementIndex = itemBounds[elementIndex];
        const domRect = getBoundsFuncForConfig(nodePositionSubConfig)(element);
        if (!isSameBoundingRect(domRect, previousBoundsForThisElementIndex)) {
          // Found something that is in a new place. Bailing from further checking and executing callback

          onPositionChanged(item, {
            nodePositionSubConfig
          });
          onCancelAndClearAllDeferredTimers({
            nodePositionSubConfig
          });
        } else {
          if (shouldDeferNextIterationBatch) {
            // Defer checking next node bounding rect to yield back to main thread for performance reasons
            const timerId = setTimeout(() => {
              incrementalSearchForChangedBounds(onScreenTuplesToCheck, index + 1, options);
            }, 0);
            onDeferWork({
              nodePositionSubConfig,
              timerId
            });
          } else {
            incrementalSearchForChangedBounds(onScreenTuplesToCheck, index + 1, options);
          }
        }
      }
    }
  } else {
    // Ran out of nodes to check
    onCancelAndClearAllDeferredTimers({
      nodePositionSubConfig
    });
    onFinishWithNoChange({
      nodePositionSubConfig
    });
  }
};